import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export type ChatSidePanelChannel = 'workshop' | 'whatsapp';

interface ChatSidePanelContextValue {
  readonly open: boolean;
  readonly channel: ChatSidePanelChannel;
  readonly openChannel: (channel: ChatSidePanelChannel) => void;
  readonly close: () => void;
  readonly toggle: () => void;
  readonly setChannel: (channel: ChatSidePanelChannel) => void;
}

const ChatSidePanelContext = createContext<ChatSidePanelContextValue | null>(null);

export function ChatSidePanelProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<ChatSidePanelChannel>('whatsapp');

  const openChannel = useCallback((next: ChatSidePanelChannel) => {
    setChannel(next);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((current) => !current);
  }, []);

  const value = useMemo(
    () => ({ open, channel, openChannel, close, toggle, setChannel }),
    [open, channel, openChannel, close, toggle],
  );

  return <ChatSidePanelContext.Provider value={value}>{children}</ChatSidePanelContext.Provider>;
}

export function useChatSidePanel(): ChatSidePanelContextValue {
  const value = useContext(ChatSidePanelContext);
  if (value === null) {
    throw new Error('useChatSidePanel must be used within ChatSidePanelProvider');
  }
  return value;
}
